import { useState } from 'react';

interface Props {
    title?: string;
    onSubmit: (out: { reason: string; notes: string }) => void | Promise<void>;
    onCancel: () => void;
}

const PRESET_REASONS = [
    'Fighting / violence',
    'Drug activity',
    'Theft',
    'Underage / fake ID',
    'Aggressive behavior',
    'Repeated rule violations',
    'Other (see notes)',
];

/**
 * Shared modal for creating a new ban. Uses preset reasons + free
 * notes. Confirm disabled until reason is selected. Same visual
 * pattern as the StageName modal added in earlier work.
 */
const BanModal = ({ title, onSubmit, onCancel }: Props) => {
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = !!reason && !submitting;

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await onSubmit({ reason, notes });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={title || 'Ban guest'}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9000,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onCancel();
            }}
        >
            <div
                style={{
                    background: '#1a1a22',
                    color: '#fff',
                    border: '1px solid #ff2d2d',
                    borderRadius: 12,
                    padding: '24px 28px',
                    minWidth: 420,
                    maxWidth: '92vw',
                    boxShadow: '0 18px 48px rgba(0,0,0,0.7)',
                }}
            >
                <h2
                    style={{
                        marginTop: 0,
                        marginBottom: 16,
                        color: '#ff5555',
                        letterSpacing: 1,
                    }}
                >
                    🚫 {title || 'Ban guest'}
                </h2>
                <label
                    style={{
                        display: 'block',
                        fontWeight: 600,
                        marginBottom: 6,
                    }}
                >
                    Reason
                </label>
                <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '10px',
                        marginBottom: 16,
                        background: '#111',
                        color: '#fff',
                        border: '1px solid #555',
                        borderRadius: 6,
                        fontSize: 15,
                    }}
                >
                    <option value="">— select reason —</option>
                    {PRESET_REASONS.map((r) => (
                        <option key={r} value={r}>
                            {r}
                        </option>
                    ))}
                </select>

                <label
                    style={{
                        display: 'block',
                        fontWeight: 600,
                        marginBottom: 6,
                    }}
                >
                    Notes (optional)
                </label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="Detail / context — visible only to admins"
                    style={{
                        width: '100%',
                        padding: '10px',
                        marginBottom: 18,
                        background: '#111',
                        color: '#fff',
                        border: '1px solid #555',
                        borderRadius: 6,
                        fontFamily: 'inherit',
                        fontSize: 14,
                        boxSizing: 'border-box',
                    }}
                />

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 10,
                    }}
                >
                    <button
                        onClick={onCancel}
                        type="button"
                        style={{
                            padding: '10px 18px',
                            background: '#333',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={!canSubmit}
                        type="button"
                        style={{
                            padding: '10px 18px',
                            background: canSubmit ? '#a31a1a' : '#a31a1a66',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                            fontSize: 14,
                            fontWeight: 700,
                            letterSpacing: 1,
                        }}
                    >
                        {submitting ? 'Banning…' : '🚫 Confirm ban'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BanModal;
